/**
 * API client for the Omega backend.
 *
 * Uses VITE_API_URL at build time; falls back to relative path (for proxied dev).
 * JWT token is stored after login and sent as Bearer token.
 */

const BASE = (import.meta.env.VITE_API_URL || "/api").replace(/\/$/, "");

/** Read the stored JWT (empty string when none / no storage). */
export function getToken() {
  if (typeof localStorage === "undefined") return "";
  return localStorage.getItem("omega_token") || "";
}

/** Store the JWT token after successful login. */
export function setToken(token) {
  localStorage.setItem("omega_token", token);
}

/** Clear the stored token (logout). */
export function clearToken() {
  localStorage.removeItem("omega_token");
}

/** Check if a token is stored. */
export function hasToken() {
  return !!getToken();
}

function headers() {
  const h = { "Content-Type": "application/json" };
  const t = getToken();
  if (t) h["Authorization"] = `Bearer ${t}`;
  return h;
}

/**
 * The single readable message for ANY response whose body is not the JSON the
 * Console expects: an SPA index.html served because a request lost its `/api`
 * prefix (HTTP 200 text/html), a proxy/gateway error page, or a stale-session
 * redirect. Surfacing this instead of the raw `Unexpected token '<', "<!DOCTYPE"`
 * parse error keeps the failure legible (heuristic 9: error recovery) — no
 * em dashes, names a cause and a next action.
 */
export const NON_JSON_MESSAGE =
  "Cannot reach the Omega API (unexpected non-JSON response). Your session may be stale, so please hard-refresh.";

/**
 * Build the Error thrown for ANY response whose body is not the JSON the Console expects.
 *
 * The bare `NON_JSON_MESSAGE` carried no diagnostic information at all, and that cost real
 * time: the Billing -> Credit view failed on Dev with this exact sentence, and nothing in the
 * UI, in a screenshot, or on the thrown Error could say whether it was a 404, an HTML error
 * page from the load balancer, or an empty body. The body is what failed to parse, so the body
 * cannot answer it. Three facts that survive a non-JSON response can:
 *
 *  - `err.status`, ALSO appended to `.message`. The message carries it because this failure is
 *    reported by screenshot far more often than by stack trace, so the status has to be on the
 *    pixels and not only on the object.
 *  - `err.request_id`, read from the `X-Request-Id` RESPONSE HEADER that RequestIdMiddleware
 *    sets on every response this application produces. Its PRESENCE is the signal worth having:
 *    a response carrying one came from our app, and one without it never reached the app at all
 *    (an edge or load-balancer error page). Deliberately NOT appended to the message, because it
 *    is 32 hex characters and a customer-facing banner is the wrong place for it.
 *  - `err.code === "non_json_response"`, so a caller branches on a stable code rather than
 *    string-matching the sentence.
 *
 * Defensive about `res` because this seam is unit-tested with hand-built response doubles that
 * do not always carry a `headers` object.
 */
function nonJsonError(res) {
  const status = Number(res?.status);
  const hasStatus = Number.isFinite(status) && status > 0;
  const err = new Error(hasStatus ? `${NON_JSON_MESSAGE} (HTTP ${status})` : NON_JSON_MESSAGE);
  err.code = "non_json_response";
  if (hasStatus) err.status = status;
  const requestId = typeof res?.headers?.get === "function" ? res.headers.get("X-Request-Id") : null;
  if (requestId) err.request_id = requestId;
  return err;
}

/**
 * Window event fired when an ALREADY-authenticated call's credential is
 * rejected (session expiry). The app (useAuth) listens for it to clear local
 * auth state and route back to the login screen. A CustomEvent-free plain Event
 * keeps api.js free of React while staying testable via window.dispatchEvent.
 */
export const SESSION_EXPIRED_EVENT = "omega:session-expired";

/**
 * Window event fired when a call has just REVOKED the current session on purpose
 * (F14 FR11/FR12: an authenticated password change increments
 * `users.credential_version`, which invalidates every JWT minted before it,
 * including the one that authorised the change).
 *
 * Deliberately separate from SESSION_EXPIRED_EVENT even though both end in a
 * logout. That one means "your credential stopped working, we do not know why";
 * this one means "you changed your password, so sign in again" — a different
 * sentence for the user, and a state the app KNOWS rather than infers from a
 * 401. Keeping them apart is also what lets useAuth show the honest notice
 * instead of a generic expiry message.
 */
export const PASSWORD_CHANGED_EVENT = "omega:password-changed";

/**
 * Window event fired when the backend refuses an authenticated call with
 * `403 registration_incomplete` (E23.6): the token is valid but is a registration
 * session, which every customer route refuses until a card is on file. useAuth
 * listens for it and shows the card step (E23.9 FR9).
 *
 * Unlike the two events above it ends nothing. The session is valid, only limited,
 * and its token is the credential the card step itself needs, so the token is
 * deliberately KEPT.
 */
export const REGISTRATION_INCOMPLETE_EVENT = "omega:registration-incomplete";

/**
 * Window event fired when ANY call is refused with `503 platform_maintenance`
 * (OMEGA-1024 FR11): an operator has stopped new workload platform-wide. useAuth
 * listens for it and App renders the maintenance notice ahead of every other view.
 *
 * This is the SECOND of the two signals FR11 converges onto one boolean; the first
 * is the `GET /platform-status` poll in useAuth. The poll alone would leave up to a
 * minute in which the customer presses Submit and reads a bare error, so the
 * refusal itself has to be able to move the UI.
 *
 * Like REGISTRATION_INCOMPLETE_EVENT it ends nothing: the token is untouched and
 * the session stays live, which is what lets the notice carry the in-flight panel
 * (FR21) and what makes the return to the shell on the next clear poll free (FR12).
 *
 * Deliberately carries NO payload. The refusal's own `message` is the API's generic
 * sentence, not the operator's customer-facing message, and rendering one where the
 * other belongs is the kind of near-miss copy FR14 exists to avoid. The listener
 * re-reads `GET /platform-status` instead, which is the one authority for the words
 * shown and for when they stop being true.
 */
export const MAINTENANCE_EVENT = "omega:platform-maintenance";

/**
 * Drop the now-dead JWT and announce the deliberate revocation.
 *
 * Lives at the api.js seam rather than in the component that happens to call
 * changePassword, for the same reason maybeSignalSessionExpiry does: the
 * revocation is a property of the REQUEST, not of one caller. A second call
 * site added later inherits the correct behaviour instead of having to
 * re-implement it (and, more likely, forget to).
 */
function signalPasswordChanged() {
  clearToken();
  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    window.dispatchEvent(new Event(PASSWORD_CHANGED_EVENT));
  }
}

/**
 * Backend 401 error `code`s that mean an already-authenticated request's
 * credential (JWT bearer via `get_current_user`, or a per-tenant/global key via
 * `APIKeyMiddleware`) was rejected — i.e. the session has expired or been
 * revoked. A bare 401 is NOT sufficient: the login form
 * ("Invalid email or password") and the change-password check
 * ("current password is incorrect") also return 401 but must NOT log the user
 * out, so their codes are deliberately absent from this set.
 *
 * OMEGA-590 — these are the stable machine codes on the NEW error envelope
 * (`{ code, message, request_id, details }`); they replaced the exact-string
 * `detail` matches ("Invalid or expired token" / "Invalid or missing API key")
 * the Console keyed off before the error-envelope hard cutover.
 */
const SESSION_EXPIRED_CODES = new Set([
  "invalid_token", // backend/app/core/dependencies.py get_current_user
  "invalid_api_key", // backend/app/core/middleware.py APIKeyMiddleware
]);

/**
 * Did this 401 reject the credential the request carried? Two shapes answer yes,
 * and the second is OMEGA-523.
 *
 * A PARSED body answers on its envelope `code` alone (see SESSION_EXPIRED_CODES):
 * the backend renders every 401 as the envelope, including the login and
 * change-password ones that must never force a logout, so the code is the whole
 * question and the credential adds nothing.
 *
 * A body that did NOT parse (`undefined` — an HTML error page from a proxy or
 * gateway, or an empty one) has no code to read, so the credential becomes the
 * whole question: a 401 that never reached the backend's envelope layer still
 * means something upstream refused this request, and if the request carried a
 * bearer then the session it belongs to is no longer usable. Leaving the token in
 * place strands the customer on a dead one, re-failing every call until they think
 * to hard-refresh.
 *
 * `Boolean(requestToken)` is what keeps that from becoming the false logout the
 * narrow code match exists to prevent, and `requestToken` means exactly one
 * thing: the bearer THIS REQUEST PUT ON THE WIRE, read back off its own
 * `Authorization` header by `sentCredential`. Not the token that happened to be
 * in storage when it was issued. A request that sent no credential cannot have
 * had one rejected, whatever the store held at the time, so it gets `""` and logs
 * nobody out: login and the other pre-session seams, and `resetPassword`, which
 * sends literal headers with no Authorization even while a session is live.
 *
 * That distinction is the whole point of reading the header rather than calling
 * `getToken()` here, and it is load-bearing rather than pedantic. Keying on the
 * stored token instead would log a signed-in customer out of an unrelated live
 * session the moment their password-reset POST met an upstream 401, having
 * rejected nothing of theirs. `forgotPassword` is the control that shows the
 * behaviour follows the credential and not the screen.
 *
 * A non-JSON change-password 401 DOES log out, and deliberately: it sends
 * `headers()`, so it is carrying the live bearer, and a body that never reached
 * the envelope layer cannot be the "current password is incorrect" refusal, which
 * is generated well inside it.
 */
function isRejectedCredential(body, requestToken) {
  if (body === undefined) return Boolean(requestToken);
  return SESSION_EXPIRED_CODES.has(typeof body?.code === "string" ? body.code : "");
}

/**
 * The bearer a request actually sends, read off the `init` it is about to be
 * issued with, or `""` when it sends none.
 *
 * Both header shapes are handled on purpose, even though every call site in
 * `engine/api*.js` currently passes a plain object (`headers()`, a literal, or a
 * spread of one). The failure mode of handling only that shape is silent and
 * fail-OPEN: were `headers()` ever to return a `Headers` instance, a plain
 * property read would yield `undefined`, every request would look unauthenticated,
 * and session-expiry auto-logout would quietly stop working everywhere rather than
 * break visibly in one place. An array of pairs is NOT handled, because nothing
 * produces one; if something starts to, the authenticated non-JSON 401 tests fail
 * loudly, which is the outcome this shape-tolerance is buying.
 */
function sentCredential(init) {
  const h = init?.headers;
  if (!h) return "";
  const raw = typeof h.get === "function" ? h.get("Authorization") : (h.Authorization ?? h.authorization);
  if (typeof raw !== "string" || !raw.startsWith("Bearer ")) return "";
  return raw.slice("Bearer ".length);
}

/**
 * Parse a response body as JSON, returning `undefined` ONLY when the body does
 * not actually parse — a text/html index.html from a mis-prefixed path, a
 * gateway error page, an empty body, or malformed JSON. NEVER throws: a raw
 * `Unexpected token '<'` SyntaxError can never escape to the UI.
 *
 * We attempt the parse REGARDLESS of the Content-Type header. A JSON body that
 * a proxy mislabels as text/plain, or a backend problem+json body
 * (application/problem+json), is still genuine JSON and MUST be used — losing
 * its `message`/`code` (and, for a 401, its session-expiry signal) to a header
 * quirk would be a worse bug than the raw parse error. The friendly fallback fires
 * only when parsing genuinely throws, so real HTML like `<!DOCTYPE ...>` (which
 * throws) still yields the readable NON_JSON_MESSAGE.
 */
async function safeJson(res) {
  try {
    return await res.json();
  } catch {
    return undefined; // HTML / empty / malformed — swallow the raw parse error
  }
}

/**
 * When a 401 rejected the credential the request carried (see
 * isRejectedCredential), clear the stored token and emit SESSION_EXPIRED_EVENT so
 * the app logs out and redirects to login. Deliberately narrow on a parsed body
 * (see SESSION_EXPIRED_CODES): login and change-password 401s are excluded, so
 * wrong credentials never trigger a false logout.
 *
 * `requestToken` is the bearer the request PUT ON THE WIRE, captured in scope
 * when it was issued (`sentCredential`, via fetchJson/fetchNoContent/
 * fetchJsonCoded). It carries TWO jobs, and both have to hold for the signal to
 * be correct. It decides whether a non-JSON 401 rejected a credential at all
 * (OMEGA-523), and it pins the response to its own session: a late 401 for an OLD
 * session must not clear a token that has since been re-issued (logout ->
 * re-login -> a stale 401 from the previous session lands), so if the credential
 * sent no longer matches the stored one, this response belongs to a superseded
 * session and we leave the current token intact and emit nothing.
 *
 * The two jobs overlap for a request that sent nothing while signed in
 * (`resetPassword`): `""` is falsy so the first returns, and `"" !== getToken()`
 * so the second would too. That redundancy is deliberate defence in depth, and it
 * has a testing consequence worth knowing before trusting a green suite: a mutant
 * that breaks ONE of the two leaves the other holding, so no single-point mutation
 * reddens the resetPassword case. Only breaking both does. A seam that never
 * passes a credential at all (`parseJsonOrThrow(res)` from login and the other
 * pre-session calls) yields `undefined`, where the second guard is a no-op and the
 * first has already returned.
 */
function maybeSignalSessionExpiry(status, body, requestToken) {
  if (status !== 401) return;
  if (!isRejectedCredential(body, requestToken)) return;
  if (requestToken !== undefined && requestToken !== getToken()) return;
  clearToken();
  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
  }
}

/**
 * When a 403 carries `registration_incomplete`, emit REGISTRATION_INCOMPLETE_EVENT
 * and leave the token exactly where it is.
 *
 * The superseded-session guard is the same one maybeSignalSessionExpiry applies, and
 * here it is what stops a trap rather than a false logout: useAuth marks the token
 * stored WHEN THE EVENT FIRES as refused, so a slow 403 issued under a registration
 * token and landing after completion stored a full one would otherwise hold the
 * customer on the card step with a token every route accepts.
 */
function maybeSignalRegistrationIncomplete(status, body, requestToken) {
  if (status !== 403) return;
  if (body?.code !== "registration_incomplete") return;
  if (requestToken !== undefined && requestToken !== getToken()) return;
  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    window.dispatchEvent(new Event(REGISTRATION_INCOMPLETE_EVENT));
  }
}

/**
 * When a 503 carries `platform_maintenance`, emit MAINTENANCE_EVENT and leave the
 * token exactly where it is (OMEGA-1024 FR11).
 *
 * No superseded-session guard, and the omission is deliberate rather than an
 * oversight: the other two signals describe THIS session's credential, so a late
 * response about a replaced one has to be dropped. Maintenance is a property of the
 * PLATFORM. The state it reports is equally true whoever is signed in, and a refusal
 * that landed a moment after a re-login still means new workload is stopped. Nor does
 * it take a `requestToken` argument at all, because the gate refuses an unauthenticated
 * register as readily as an authenticated submit.
 */
function maybeSignalMaintenance(status, body) {
  if (status !== 503) return;
  if (body?.code !== "platform_maintenance") return;
  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    window.dispatchEvent(new Event(MAINTENANCE_EVENT));
  }
}

/**
 * Turn a non-OK response into a readable Error and, for a session-expiry 401 on
 * an authenticated call, trigger auto-logout. A JSON error body flows through
 * readDetail (the NEW envelope's human `message`); a non-JSON error page
 * (proxy/gateway/stale HTML) yields the friendly NON_JSON_MESSAGE instead of a
 * raw parse error. Always throws. `requestToken` is the credential the request
 * carried (see maybeSignalSessionExpiry).
 *
 * OMEGA-590 — the thrown Error carries the envelope's stable fields so ANY
 * caller (not just the coded managed-upload seam) can branch on them:
 * `err.status`, `err.code` (e.g. useAuth keys the L01 verify-your-email branch
 * off `err.code === "email_not_verified"`), `err.request_id` (== the
 * `X-Request-Id` header, for support diagnostics), and `err.details`.
 */
async function throwResponseError(res, requestToken) {
  const body = await safeJson(res);
  // BEFORE the non-JSON bail-out, because an unparseable 401 is itself a
  // session-expiry signal (OMEGA-523) and this early return used to outrun it.
  // The other two signals stay after it and that asymmetry is deliberate: they
  // key off a code an upstream 403/503 page cannot be carrying, so for them a
  // body that did not parse is genuinely no signal rather than a lost one.
  maybeSignalSessionExpiry(res.status, body, requestToken);
  if (body === undefined) {
    throw nonJsonError(res);
  }
  maybeSignalRegistrationIncomplete(res.status, body, requestToken);
  maybeSignalMaintenance(res.status, body);
  throw errorFromEnvelope(res.status, body);
}

/**
 * Build a readable Error from a parsed NEW error envelope (OMEGA-590), attaching
 * the stable diagnostic fields (`status`, `code`, `request_id`, `details`). The
 * `.message` is the envelope's human `message` via readDetail (never
 * "[object Object]"), falling back to `HTTP <status>`.
 */
function errorFromEnvelope(status, body) {
  const err = new Error(readDetail(body) || `HTTP ${status}`);
  err.status = status;
  if (body && typeof body === "object") {
    if (typeof body.code === "string") err.code = body.code;
    if (typeof body.request_id === "string") err.request_id = body.request_id;
    if (body.details !== undefined) err.details = body.details;
  }
  return err;
}

/**
 * The single seam every JSON-returning api.js caller uses. On a 2xx it returns
 * the parsed JSON body; on a non-2xx it throws a readable Error (and, for a
 * session-expiry 401, triggers auto-logout). A non-JSON body — the classic
 * "hit index.html because the path lost its /api prefix" 200 — yields the
 * friendly NON_JSON_MESSAGE, never a raw `Unexpected token '<'`. `requestToken`
 * is the credential the request carried (see maybeSignalSessionExpiry).
 */
async function parseJsonOrThrow(res, requestToken) {
  if (!res.ok) {
    await throwResponseError(res, requestToken);
  }
  const body = await safeJson(res);
  if (body === undefined) {
    throw nonJsonError(res);
  }
  return body;
}

/**
 * Issue a request and parse its JSON body (throwing readably on a non-2xx),
 * capturing in scope the bearer THE REQUEST ITSELF CARRIES.
 *
 * Reading it from `init` rather than calling `getToken()` is what makes the
 * captured value answer "whose credential did this 401 reject?" instead of the
 * weaker "was anyone signed in when this went out?". The two agree for every
 * authenticated call, since `headers()` builds the header from the same store,
 * and they disagree exactly where it matters: a request that deliberately sends
 * no Authorization (`resetPassword`) must not be able to log out a session it
 * never used. Capturing BEFORE the fetch is what additionally lets
 * maybeSignalSessionExpiry tell a genuine current-session expiry from a delayed
 * 401 for a superseded one — a stale 401 arriving after the user has re-logged-in
 * must NOT wipe the freshly-issued token.
 */
async function fetchJson(url, init) {
  const requestToken = sentCredential(init);
  const res = await fetch(url, init);
  return parseJsonOrThrow(res, requestToken);
}

/**
 * Same credential-capture as fetchJson, for the 204 No Content endpoints
 * (DELETE project / API key, change password, reset password): on a 2xx there is
 * no body to parse, so resolve undefined; on a non-2xx throw readably (and, for a
 * genuine current-session expiry, signal auto-logout). This seam carries both
 * sides of the distinction above: `changePassword` sends `headers()` and so can
 * be expired out, `resetPassword` sends none and so cannot.
 */
async function fetchNoContent(url, init) {
  const requestToken = sentCredential(init);
  const res = await fetch(url, init);
  if (!res.ok) await throwResponseError(res, requestToken);
  return undefined;
}

/**
 * The coded-error seam for the E02.7 managed-upload lifecycle. Same JSON parse +
 * session-expiry handling as fetchJson, but on a non-2xx it throws an Error that
 * ALSO carries the backend's stable coded fields so callers (useManagedUpload)
 * can branch on them. OMEGA-590 — these now read the NEW error envelope
 * (`{ code, message, request_id, details }`): `err.code` (the stable machine
 * code, e.g. "queue_full" / "idempotency_conflict"), `err.field`
 * (`details.field` when present), `err.status`, `err.request_id` (== the
 * `X-Request-Id` response header, for support diagnostics), and `err.details`.
 * The `.message` is still the readable `readDetail(body)` sentence (the
 * envelope's human `message`, never "[object Object]"), and a non-JSON body
 * still yields NON_JSON_MESSAGE.
 *
 * Confirm returns HTTP 200 even on a validation failure (the failure rides in
 * the body), so a caller reads the body on 2xx and only lands here for a genuine
 * non-2xx (409/410/422/429/503).
 */
async function fetchJsonCoded(url, init) {
  const requestToken = sentCredential(init);
  const res = await fetch(url, init);
  if (!res.ok) {
    const body = await safeJson(res);
    // BEFORE the non-JSON bail-out, for the reason throwResponseError states above: an
    // unparseable 401 is itself a session-expiry signal (OMEGA-523), and this early return
    // used to outrun it. The throw is `nonJsonError` rather than a bare Error because that
    // is this branch's OMEGA-516 change, which every other throw site in this file already
    // uses and which three api.test.js cases pin by message. Neither side of the merge had
    // both halves; throwResponseError does, and this now matches it.
    maybeSignalSessionExpiry(res.status, body, requestToken);
    if (body === undefined) throw nonJsonError(res);
    maybeSignalRegistrationIncomplete(res.status, body, requestToken);
    maybeSignalMaintenance(res.status, body);
    const err = errorFromEnvelope(res.status, body);
    const d = body?.details;
    if (d && typeof d === "object" && !Array.isArray(d) && d.field) {
      err.field = d.field;
    }
    throw err;
  }
  const body = await safeJson(res);
  if (body === undefined) throw nonJsonError(res);
  return body;
}


/**
 * Render the NEW error envelope readably (OMEGA-590). Every backend error is now
 * `{ code, message, request_id, details }` — surface the human `message`, and
 * fall back to the stable machine `code` when a message is somehow absent, so a
 * caller always gets a legible string (never "[object Object]"). Passed the
 * parsed response body; a bare string is returned as-is for resilience.
 */
export function readDetail(err) {
  if (err == null) return "";
  if (typeof err === "string") return err;
  if (typeof err.message === "string" && err.message) return err.message;
  if (typeof err.code === "string" && err.code) return err.code;
  return "";
}

// Shared with the sibling api*.js modules, which are this client's other halves. Deliberately
// NOT re-exported by api.js: the client's public surface is what it was before the split.
export { BASE, headers, signalPasswordChanged, parseJsonOrThrow, fetchJson, fetchNoContent, fetchJsonCoded };
